import { Result, Uuid } from '@standardnotes/domain-core';
import { EncryptedPayload, HistoryEntry, isErrorDecryptingPayload, checkRemotePayloadAllowed, PayloadTimestampDefaults, } from '@standardnotes/models';
export class GetRevision {
    constructor(revisionManager, encryptionService) {
        this.revisionManager = revisionManager;
        this.encryptionService = encryptionService;
    }
    async execute(dto) {
        var _a, _b;
        const itemUuidOrError = Uuid.create(dto.itemUuid);
        if (itemUuidOrError.isFailed()) {
            return Result.fail(`Could not get revision: ${itemUuidOrError.getError()}`);
        }
        const itemUuid = itemUuidOrError.getValue();
        const revisionUuidOrError = Uuid.create(dto.revisionUuid);
        if (revisionUuidOrError.isFailed()) {
            return Result.fail(`Could not get revision: ${revisionUuidOrError.getError()}`);
        }
        const revisionUuid = revisionUuidOrError.getValue();
        let revision;
        try {
            revision = await this.revisionManager.getRevision(itemUuid, revisionUuid);
        }
        catch (error) {
            return Result.fail(`Could not get revision: ${error.message}`);
        }
        if (!revision) {
            return Result.fail('Could not get revision: Revision not found');
        }
        const serverPayload = new EncryptedPayload({
            ...PayloadTimestampDefaults(),
            uuid: revision.uuid,
            content: revision.content,
            enc_item_key: revision.enc_item_key,
            items_key_id: revision.items_key_id,
            auth_hash: revision.auth_hash,
            content_type: revision.content_type,
            updated_at: new Date(revision.updated_at),
            created_at: new Date(revision.created_at),
            key_system_identifier: (_a = revision.key_system_identifier) !== null && _a !== void 0 ? _a : undefined,
            shared_vault_uuid: (_b = revision.shared_vault_uuid) !== null && _b !== void 0 ? _b : undefined,
            waitingForKey: false,
            errorDecrypting: false,
        });
        /**
         * When an item is duplicated, its revisions also carry over to the newly created item.
         * However since the new item has a different UUID than the source item, we must decrypt
         * these olders revisions (which have not been mutated after copy) with the source item's
         * uuid.
         */
        const embeddedParams = this.encryptionService.getEmbeddedPayloadAuthenticatedData(serverPayload);
        const sourceItemUuid = embeddedParams === null || embeddedParams === void 0 ? void 0 : embeddedParams.u;
        const payload = serverPayload.copy({
            uuid: sourceItemUuid || revision.item_uuid,
        });
        const remotePayloadAllowedResult = checkRemotePayloadAllowed(payload);
        if (remotePayloadAllowedResult.disallowed !== undefined) {
            return Result.fail(`Remote payload is disallowed: ${JSON.stringify(payload)}`);
        }
        const encryptedPayload = new EncryptedPayload(payload);
        const decryptedPayload = await this.encryptionService.decryptSplitSingle({
            usesItemsKeyWithKeyLookup: { items: [encryptedPayload] },
        });
        if (isErrorDecryptingPayload(decryptedPayload)) {
            return Result.fail('Could not decrypt revision.');
        }
        return Result.ok(new HistoryEntry(decryptedPayload));
    }
}
